import { useEffect, useState } from "react";
import { Loader2, MessageCircle, Save, Send, Eye, EyeOff, CheckCircle2, XCircle, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { fmtDateTime, fmtDate } from "@/lib/format";

function SecretInput({ value, onChange, placeholder, testId }) {
  const [show, setShow] = useState(false);
  return (
    <div className="secret-input">
      <input className="rj-input" type={show ? "text" : "password"} value={value} autoComplete="off"
        onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={testId} />
      <button type="button" onClick={() => setShow(!show)} aria-label="Tampilkan" data-testid={`${testId}-toggle`}>
        {show ? <EyeOff size={15} /> : <Eye size={15} />}
      </button>
    </div>
  );
}

function TestResult({ t }) {
  if (!t) return null;
  const ok = t.key_ok && t.sent;
  return (
    <div className={`wa-test-result ${ok ? "ok" : "bad"}`} data-testid="wa-test-result">
      {ok ? <CheckCircle2 size={16} /> : <XCircle size={16} />}
      <div>
        <strong>{ok ? `Koneksi sukses — pesan tes terkirim ke ${t.to}` : "Tes koneksi gagal"}</strong>
        <small>API key: {t.key_ok ? "valid" : "tidak valid"}{t.key_info?.expires_on ? ` · aktif s/d ${fmtDate(t.key_info.expires_on)}` : ""}
          {(t.key_info?.licenses || []).map((l, i) => ` · ${l.number || "nomor"} ${l.is_connected ? "terhubung" : "tidak terhubung"}`).join("")}</small>
        {t.error && <small className="wa-test-error">{t.error}</small>}
        <small>Dites {fmtDateTime(t.at)} oleh {t.by}</small>
      </div>
    </div>
  );
}

export function WhatsAppConnection({ onChanged }) {
  const [s, setS] = useState(null);
  const [apiKey, setApiKey] = useState("");
  const [numberKey, setNumberKey] = useState("");
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(null);

  useEffect(() => { api.get("/settings/whatsapp").then((r) => setS(r.data.data)).catch((e) => toast.error(apiError(e))); }, []);

  const save = async () => {
    if (!s.configured && (!apiKey.trim() || !numberKey.trim())) { toast.error("Isi API Key dan Number Key"); return; }
    setBusy("save");
    try {
      const r = await api.put("/settings/whatsapp", { api_key: apiKey.trim() || null, number_key: numberKey.trim() || null });
      setS(r.data.data); setApiKey(""); setNumberKey(""); onChanged?.();
      toast.success("Koneksi WhatsApp disimpan. Klik 'Tes Kirim Pesan' untuk memastikan koneksi berjalan.");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(null); }
  };
  const test = async () => {
    setBusy("test");
    try {
      const r = await api.post("/settings/whatsapp/test", { phone: phone || null });
      const t = r.data.data;
      setS((x) => ({ ...x, last_test: t }));
      t.key_ok && t.sent ? toast.success(`Pesan tes terkirim ke ${t.to}`) : toast.error(t.error || "Tes koneksi gagal");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(null); }
  };
  const remove = async () => {
    if (!window.confirm("Hapus API Key & Number Key Watzap yang disimpan di aplikasi?")) return;
    setBusy("del");
    try { const r = await api.delete("/settings/whatsapp"); setS(r.data.data); onChanged?.(); toast.success("Koneksi WhatsApp dihapus"); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(null); }
  };

  if (!s) return <div className="panel"><div className="rj-loading"><Loader2 className="spin" /> Memuat…</div></div>;
  const lt = s.last_test;
  const pill = !s.configured ? ["rj-pill-mock", "Belum diatur"]
    : !lt ? ["rj-pill-mock", "Tersimpan · belum dites"]
      : lt.key_ok && lt.sent ? ["rj-pill-live", "Terhubung · tes sukses"] : ["rj-pill-bad", "Tes gagal"];
  return (
    <div className="panel reminder-panel" data-testid="wa-connection-panel">
      <div className="panel-head"><div><h3><MessageCircle size={16} style={{ verticalAlign: -3 }} /> Koneksi WhatsApp (Watzap.id)</h3>
        <p>Dipakai untuk pengingat jatuh tempo via WhatsApp. API Key & Number Key ada di member area Watzap → Integrations.</p></div>
        <span className={`rj-pill ${pill[0]}`} data-testid="wa-connection-status">{pill[1]}{s.configured && s.source === "env" ? " (env)" : ""}</span>
      </div>
      {s.configured && (
        <div className="wa-current" data-testid="wa-current-keys">
          <span>API Key <b>{s.api_key_masked}</b></span><span>Number Key <b>{s.number_key_masked}</b></span>
          {s.updated_at && <small>Diubah {fmtDateTime(s.updated_at)} oleh {s.updated_by}</small>}
        </div>
      )}
      <div className="reminder-fields">
        <label className="rj-field">API Key {s.configured && <small>(kosongkan jika tidak diubah)</small>}
          <SecretInput value={apiKey} onChange={setApiKey} placeholder="API key Watzap" testId="wa-api-key-input" />
        </label>
        <label className="rj-field">Number Key {s.configured && <small>(kosongkan jika tidak diubah)</small>}
          <SecretInput value={numberKey} onChange={setNumberKey} placeholder="Number key Watzap" testId="wa-number-key-input" />
        </label>
      </div>
      <div className="reminder-actions">
        <button className="rj-btn rj-btn-green" onClick={save} disabled={!!busy || (!apiKey.trim() && !numberKey.trim())} data-testid="wa-save-btn">
          {busy === "save" ? <Loader2 size={16} className="spin" /> : <><Save size={16} /> Simpan Koneksi</>}
        </button>
        {s.source === "app" && (
          <button className="rj-btn rj-btn-ghost" onClick={remove} disabled={!!busy} data-testid="wa-delete-btn"><Trash2 size={15} /> Hapus</button>
        )}
      </div>
      <div className="wa-test-row">
        <label className="rj-field">Nomor WhatsApp tujuan tes
          <input className="rj-input" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="0812xxxxxxx (kosong = nomor uji Pengingat)" data-testid="wa-test-phone" />
        </label>
        <button className="rj-btn rj-btn-primary" onClick={test} disabled={!!busy || !s.configured} data-testid="wa-test-btn"
          title={s.configured ? "Cek API key lalu kirim pesan tes" : "Simpan koneksi terlebih dahulu"}>
          {busy === "test" ? <Loader2 size={16} className="spin" /> : <><Send size={16} /> Tes Kirim Pesan</>}
        </button>
      </div>
      <TestResult t={s.last_test} />
    </div>
  );
}
